/**
 * Worksite Detail Page — /office/worksites/<pk>/
 *
 * Wires two dynamic modals on this page:
 *
 * 1. "Attach DDT reference" — replaces the legacy dedicated page.
 *    Triggered by ``[data-attach-ddt-open]`` (red compliance banner,
 *    green "voluntary DDT" link, empty DDT history state).
 * 2. "Assign subcontractor" (Slice E) — opens the
 *    ``SubcontractorAssignment`` form. Triggered by
 *    ``[data-assign-subcontractor-open]`` (header CTA + empty-state
 *    link inside the subcontractor card).
 *
 * Lifecycle (mirrors invoice-detail.js):
 *   - Modal helpers bound at ``initWorksiteDetail()`` time.
 *   - On ``htmx:afterSettle`` (boosted nav or after a soft-reload
 *     triggered by a successful save), the previous modal <div>s are
 *     detached from the DOM and replaced by fresh ones. The cached
 *     helpers still point at the orphan nodes, so we null them out and
 *     re-init. Without this reset, the buttons would silently no-op
 *     until a hard refresh (cf. § 9 of ``form-modals.mdc``).
 *
 * Template contract:
 *   - Root container: ``[data-auto-init="worksiteDetail"
 *                        data-attach-ddt-url="..."
 *                        data-attach-ddt-title="..."
 *                        data-attach-ddt-voluntary-title="..."
 *                        data-assign-subcontractor-url="..."
 *                        data-assign-subcontractor-title="..."]``
 *   - Modal shells: ``#attach-ddt-modal`` and
 *     ``#assign-subcontractor-modal`` with ``.modal-content`` /
 *     ``.modal-title`` / ``.modal-close-btn``.
 *   - Triggers: ``[data-attach-ddt-open]``,
 *     ``[data-assign-subcontractor-open]``. Add
 *     ``[data-attach-ddt-voluntary]`` on the DDT trigger to use the
 *     "Declare a voluntary DDT" title instead of the default one.
 */

import { createDynamicModal } from '../utils/ui/dynamic-modal.js';

let globalBound = false;
let attachDdtModal = null;
let assignSubcontractorModal = null;

function initAttachDdtModal() {
    if (attachDdtModal) return;
    attachDdtModal = createDynamicModal({
        modalId: 'attach-ddt-modal',
        onContentInjected: function () {
            // The DDT form has no datepicker, no custom-select, no
            // TipTap — just a text input + textarea + checkbox. Still,
            // re-init Flowbite widgets within the modal scope so that
            // any future addition (e.g. a tooltip on the regex helper
            // line) works without extra wiring.
            if (typeof window.reinitFlowbiteWidgets === 'function') {
                var modalEl = document.getElementById('attach-ddt-modal');
                if (modalEl) window.reinitFlowbiteWidgets(modalEl);
            }
        },
        // Default onSuccess (no override): close the modal + call
        // ``window._softReload()`` which re-fetches the current
        // worksite detail page in place. The compliance banner flips
        // red → green and the Django ``messages.success`` rendered by
        // ``ddt_create`` shows up as a toast on the soft-reloaded body.
    });
}

function initAssignSubcontractorModal() {
    if (assignSubcontractorModal) return;
    assignSubcontractorModal = createDynamicModal({
        modalId: 'assign-subcontractor-modal',
        onContentInjected: function () {
            // The subcontractor form has a ``select.form-select`` (the
            // subcontractor company picker) and Flowbite datepickers.
            // Both need re-init on every inject (initial GET + 422
            // re-render) since the helper replaces ``.modal-content``
            // innerHTML wholesale. Mirrors the canonical pattern in
            // ``subcontractor-settings.js`` — see ``form-modals.mdc`` §5.
            if (typeof window.initCustomSelects === 'function') {
                window.initCustomSelects();
            }
            if (typeof window.reinitFlowbiteWidgets === 'function') {
                var modalEl = document.getElementById('assign-subcontractor-modal');
                if (modalEl) window.reinitFlowbiteWidgets(modalEl);
            }
        },
    });
}

function handleDelegatedClick(evt) {
    var t = evt.target;
    if (!t || !t.closest) return;

    var root = document.querySelector('[data-auto-init="worksiteDetail"]');
    if (!root) return;

    var ddtBtn = t.closest('[data-attach-ddt-open]');
    if (ddtBtn) {
        evt.preventDefault();
        if (!attachDdtModal) return;
        var url = root.dataset.attachDdtUrl;
        if (!url) return;
        var title = root.dataset.attachDdtTitle || 'Attach DDT reference';
        if (ddtBtn.hasAttribute('data-attach-ddt-voluntary')) {
            title = root.dataset.attachDdtVoluntaryTitle || title;
        }
        attachDdtModal.open(url, title);
        return;
    }

    var subBtn = t.closest('[data-assign-subcontractor-open]');
    if (subBtn) {
        evt.preventDefault();
        if (!assignSubcontractorModal) return;
        var subUrl = root.dataset.assignSubcontractorUrl;
        if (!subUrl) return;
        var subTitle = root.dataset.assignSubcontractorTitle || 'Assign subcontractor';
        assignSubcontractorModal.open(subUrl, subTitle);
        return;
    }

    // Linked jobs — navigate directly to the unified Job detail page
    // (Configuration tab is the default landing surface ; the page
    // itself handles the terminal-status redirect-to-list guard, so
    // clicking a cancelled / completed job still bounces the operator
    // to the jobs list — same UX as before).
    //
    // PO 2026-05-22 — was previously ``window.showJobDrawer(jobId)``.
    // Each row now exposes its target URL on ``data-job-detail-url``
    // (built server-side in ``worksites/detail.html``) so this delegate
    // doesn't need to know the URL pattern at all.
    var jobBtn = t.closest('[data-job-detail-url]');
    if (jobBtn) {
        evt.preventDefault();
        var url = jobBtn.dataset.jobDetailUrl;
        if (url) {
            window.location.assign(url);
        }
    }
}

// Keyboard a11y for clickable rows: ``role="button"`` + ``tabindex=0``
// on the <tr> makes it focusable, but the browser doesn't synthesize
// a click on Enter / Space the way it would for a real <button>. We
// translate those keypresses into the same navigation intent here.
// Only ``[data-job-detail-url]`` opt-in elements participate so we
// don't accidentally swallow keystrokes elsewhere on the page.
function handleDelegatedKey(evt) {
    if (evt.key !== 'Enter' && evt.key !== ' ' && evt.key !== 'Spacebar') return;
    var t = evt.target;
    if (!t || !t.closest) return;
    var jobBtn = t.closest('[data-job-detail-url]');
    if (!jobBtn) return;
    evt.preventDefault();
    var url = jobBtn.dataset.jobDetailUrl;
    if (url) {
        window.location.assign(url);
    }
}

function handleAfterSettle(evt) {
    var target = evt.detail && evt.detail.target;
    if (!target || target.id !== 'main-content') return;
    // ``#main-content`` was just swapped, the previous modal shells are
    // orphans now. Re-bind. See § 9 of ``form-modals.mdc``.
    attachDdtModal = null;
    assignSubcontractorModal = null;
    initAttachDdtModal();
    initAssignSubcontractorModal();
}

function bindGlobalDelegation() {
    if (globalBound) return;
    globalBound = true;
    document.body.addEventListener('click', handleDelegatedClick);
    document.body.addEventListener('keydown', handleDelegatedKey);
    document.body.addEventListener('htmx:afterSettle', handleAfterSettle);
}

export function initWorksiteDetail() {
    // CRITICAL: invalidate helper caches before re-creating them. See
    // § 9 of ``form-modals.mdc`` and the matching pattern in
    // ``invoice-detail.js::initInvoiceDetail``.
    attachDdtModal = null;
    assignSubcontractorModal = null;
    bindGlobalDelegation();
    initAttachDdtModal();
    initAssignSubcontractorModal();
}

export function autoInitWorksiteDetail() {
    var root = document.querySelector('[data-auto-init="worksiteDetail"]');
    if (!root) return;
    initWorksiteDetail();
}
